import React, { useEffect, useState } from "react";
import {
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  getDoc,
  runTransaction,
} from "firebase/firestore";
import { db } from "../../firebase";
import { buildAffiliateTurnoverUpdate } from "../../lib/affiliateBonus";
import toast from "react-hot-toast";
import ExitConfirmModal from "./ExitConfirmModal";

const WIN_MATRIX = {
  rock: "scissors",
  paper: "rock",
  scissors: "paper",
};

function resolveWinnerUid(game) {
  if (!game?.winner) return "";
  if (game.winner === "uid1") return game.player1?.userId || "";
  if (game.winner === "uid2") return game.player2?.userId || "";
  return game.winner;
}

export default function RPSGameRoom({ gameId, user, setCurrentGameId }) {
  const [game, setGame] = useState(null);
  const [myMove, setMyMove] = useState("");
  const [loading, setLoading] = useState(true);
  const [deducting, setDeducting] = useState(false);
  const [forfeitModal, setForfeitModal] = useState(false);

  // Update lastActive every 20s while in game, so you’re always “online”
  useEffect(() => {
    if (!game || game.status !== "active" || !user?.uid) return;
    const interval = setInterval(() => {
      updateLastActive(gameId, user.uid);
    }, 10000);
    return () => clearInterval(interval);
  }, [game, gameId, user?.uid]);

  async function updateLastActive(gameId, userId) {
    const gameRef = doc(db, "games", gameId);
    await updateDoc(gameRef, {
      [`lastActive.${userId}`]: Date.now(),
    });
  }

  // Fetch game state
  useEffect(() => {
    if (!gameId) return;
    const docRef = doc(db, "games", gameId);
    const unsub = onSnapshot(docRef, (snap) => {
      setGame(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsub;
  }, [gameId]);

  // Inactivity check: if opponent offline > 1min & game unfinished, you win
  useEffect(() => {
    if (!game || game.status === "finished") return;
    if (!game.player1 || !game.player2 || !user?.uid) return;

    const myUid = user.uid;

    const now = Date.now();
    const isPlayer1 = game.player1.userId === user.uid;
    const myKey = isPlayer1 ? "uid1" : "uid2";
    const oppKey = isPlayer1 ? "uid2" : "uid1";
    const opponentUid = isPlayer1 ? game.player2.userId : game.player1.userId;

    // Only if neither player has made a move yet
    const myMove = game.moves?.[myKey];
    const oppMove = game.moves?.[oppKey];

    if (
      !myMove &&
      !oppMove &&
      game.lastActive &&
      game.lastActive[opponentUid]
    ) {
      const oppLastActive = game.lastActive[opponentUid];
      // 60 sec = 60,000 ms
      // if (now - oppLastActive > 60000) {
      //   // Opponent inactive for >1min, you win!
      //   updateDoc(doc(db, "games", gameId), {
      //     status: "finished",
      //     winner: myKey,
      //     updatedAt: serverTimestamp(),
      //   });
      //   toast.success("Opponent went offline. You win!");
      // }
      if (
        now - oppLastActive > 60000 &&
        game.status === "active" &&
        !game.winner
      ) {
        handleWinByForfeit(gameId, myUid);
      }
    }
  }, [game, gameId, user?.uid]);

  async function handleWinByForfeit(gameId, winnerUid) {
    const gameRef = doc(db, "games", gameId);

    // Only process if not already finished
    const snap = await getDoc(gameRef);
    if (!snap.exists()) return;
    const gameData = snap.data();
    if (gameData.status !== "active" || !!gameData.winner) return;

    await updateDoc(gameRef, {
      status: "finished",
      winner: winnerUid,
      winReason: "opponent_timeout",
      endedAt: Date.now(),
    });
  }

  // Game end logic is the same as before...
  useEffect(() => {
    if (
      game &&
      game.moves?.uid1 &&
      game.moves?.uid2 &&
      game.status !== "finished"
    ) {
      const m1 = game.moves.uid1;
      const m2 = game.moves.uid2;
      let winner = null;
      if (m1 === m2) winner = "draw";
      else if (WIN_MATRIX[m1] === m2) winner = game.player1?.userId || "uid1";
      else winner = game.player2?.userId || "uid2";

      updateDoc(doc(db, "games", gameId), {
        status: "finished",
        winner,
        // paidOut: true,
        updatedAt: serverTimestamp(),
      });

      // Turnover logic (not entry deduction)
      [game.player1?.userId, game.player2?.userId].forEach(async (uid) => {
        if (!uid) return;
        const userRef = doc(db, "users", uid);
        await runTransaction(db, async (transaction) => {
          const snap = await transaction.get(userRef);
          if (!snap.exists()) return;
          const data = snap.data() || {};
          const wager = Number(game.betAmount || 0);
          const affiliateUpdate = buildAffiliateTurnoverUpdate(data, wager);
          transaction.update(userRef, {
            turnover: +(Number(data.turnover || 0) + wager).toFixed(2),
            turnoverCompleted: +(
              Number(data.turnoverCompleted || 0) + wager
            ).toFixed(2),
            ...affiliateUpdate.fields,
          });
        });
      });
    }
  }, [game, gameId]);

  useEffect(() => {
    if (!game || game.status !== "finished" || game.payoutProcessed) return;

    async function settlePayout() {
      const gameRef = doc(db, "games", gameId);

      await runTransaction(db, async (transaction) => {
        const freshSnap = await transaction.get(gameRef);
        if (!freshSnap.exists()) return;

        const freshGame = freshSnap.data();
        if (freshGame.payoutProcessed) return;

        const bet = Number(freshGame.betAmount || 0.5);
        const winnerUid = resolveWinnerUid(freshGame);

        if (winnerUid === "draw") {
          for (const uid of [freshGame.player1?.userId, freshGame.player2?.userId]) {
            if (!uid) continue;
            const userRef = doc(db, "users", uid);
            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) continue;
            const balance = Number(userSnap.data().balance || 0);
            transaction.update(userRef, {
              balance: +(balance + bet).toFixed(2),
            });
          }
          transaction.update(gameRef, {
            payoutProcessed: true,
            payoutAmount: bet,
            updatedAt: serverTimestamp(),
          });
          return;
        }

        if (!winnerUid) return;
        const winnerRef = doc(db, "users", winnerUid);
        const winnerSnap = await transaction.get(winnerRef);
        if (!winnerSnap.exists()) return;

        const prize = +(bet * 1.8).toFixed(2);
        const currentBalance = Number(winnerSnap.data().balance || 0);

        transaction.update(winnerRef, {
          balance: +(currentBalance + prize).toFixed(2),
        });
        transaction.update(gameRef, {
          payoutProcessed: true,
          payoutAmount: prize,
          payoutWinner: winnerUid,
          updatedAt: serverTimestamp(),
        });
      });
    }

    settlePayout().catch((error) => {
      toast.error(error?.message || "Failed to settle RPS payout.");
    });
  }, [game, gameId]);

  useEffect(() => {
    if (!game || game.status !== "finished" || game.statsUpdated) return;

    async function updatePlayerStats() {
      const { player1, player2, winner, betAmount } = game;
      if (!player1?.userId || !player2?.userId) return;
      const bet = Number(betAmount || 0.5);
      const winnerUid = resolveWinnerUid(game);

      // Define your own RPS formula, e.g.:
      // Win: +8 IQ, Draw: +2 IQ, Lose: 0 IQ
      let p1_iq = 0,
        p2_iq = 0;
      let p1_win = 0,
        p2_win = 0;
      let p1_cash = 0,
        p2_cash = 0;

      if (winnerUid === "draw") {
        p1_iq = p2_iq = 2;
        p1_cash = p2_cash = 0;
      } else if (winnerUid === player1.userId) {
        p1_iq = 8;
        p2_iq = 0;
        p1_win = 1;
        p2_win = 0;
        p1_cash = bet * 0.8;
        p2_cash = -bet;
      } else if (winnerUid === player2.userId) {
        p2_iq = 8;
        p1_iq = 0;
        p2_win = 1;
        p1_win = 0;
        p2_cash = bet * 0.8;
        p1_cash = -bet;
      }

      // Player 1 (uid1 = player1.userId)
      await updateUserStats(player1.userId, {
        iqField: "rps_iq",
        winField: "rps_wins",
        cashField: "rps_cash",
        iq: p1_iq,
        win: p1_win,
        cash: p1_cash,
      });

      // Player 2 (uid2 = player2.userId)
      await updateUserStats(player2.userId, {
        iqField: "rps_iq",
        winField: "rps_wins",
        cashField: "rps_cash",
        iq: p2_iq,
        win: p2_win,
        cash: p2_cash,
      });

      // Mark stats updated
      await updateDoc(doc(db, "games", gameId), {
        statsUpdated: true,
      });
    }

    updatePlayerStats();
  }, [game, gameId]);

  useEffect(() => {
    if (!game || !user?.uid || deducting) return;
    const isPlayer1 = game.player1 && game.player1.userId === user.uid;
    const isPlayer2 = game.player2 && game.player2.userId === user.uid;
    const betAmount = +(game.betAmount || 0.5);

    if (isPlayer1 && !game.player1Accepted) {
      deductBet("player1");
    } else if (isPlayer2 && !game.player2Accepted) {
      deductBet("player2");
    }

    async function deductBet(playerKey) {
      setDeducting(true);
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const balance = +(userSnap.data().balance || 0);
        if (balance < betAmount) {
          toast.error("Not enough balance to join!");
          setDeducting(false);
          setTimeout(() => setCurrentGameId(null), 1000);
          return;
        }
        // 👇👇 Check accepted flag before proceeding!
        if (playerKey === "player1" && game.player1Accepted) {
          setDeducting(false);
          return;
        }
        if (playerKey === "player2" && game.player2Accepted) {
          setDeducting(false);
          return;
        }
        await updateDoc(userRef, {
          balance: +(balance - betAmount).toFixed(2),
        });
        await updateDoc(doc(db, "games", gameId), {
          [`${playerKey}Accepted`]: true,
          updatedAt: serverTimestamp(),
        });
        toast.success("Entry fee deducted. Get ready to play!");
      } catch (err) {
        toast.error("Error deducting entry: " + err.message);
      }
      setDeducting(false);
    }
  }, [game, user?.uid, gameId, setCurrentGameId, deducting]);

  useEffect(() => {
    if (!game || !game.status || !game.winner) return;

    const winnerUid = resolveWinnerUid(game);

    if (game.status === "finished") {
      if (winnerUid === user.uid) {
        playSound("/sounds/win.mp3");
      } else if (winnerUid === "draw") {
        playSound("/sounds/draw.mp3");
      } else {
        playSound("/sounds/lose.mp3");
      }
    }
  }, [game, user.uid]);

  function playSound(url) {
    const audio = new window.Audio(url);
    audio.volume = 0.55;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  async function handleForfeit() {
    if (!game || game.status !== "active") {
      setCurrentGameId(null);
      return;
    }

    const winnerKey =
      game.player1.userId === user.uid ? game.player2.userId : game.player1.userId;

    await updateDoc(doc(db, "games", game.id), {
      status: "finished",
      winner: winnerKey,
      winReason: "forfeit",
      updatedAt: serverTimestamp(),
    });

    await updateDoc(doc(db, "users", user.uid), {
      currentGameId: null,
    });

    toast.error("You forfeited the game!");
    setCurrentGameId(null);
  }

  if (loading) return <div>Loading game…</div>;
  if (!game) return <div>Game not found.</div>;
  if (!game.player1 || !game.player2) {
    return (
      <div className="flex flex-col items-center pt-8">
        <div className="mb-4 text-xl font-bold text-lime-400">
          Rock Paper Scissors
        </div>
        <div className="mb-3 text-white">
          Waiting for another player to join...
        </div>
      </div>
    );
  }

  // Don’t allow moves until entry is paid by both players
  const isPlayer1 = game.player1.userId === user.uid;
  const myKey = isPlayer1 ? "uid1" : "uid2";
  const oppKey = isPlayer1 ? "uid2" : "uid1";
  const opponent = isPlayer1 ? game.player2.username : game.player1.username;

  const entryPaid = isPlayer1 ? game.player1Accepted : game.player2Accepted;
  const otherEntryPaid = isPlayer1
    ? game.player2Accepted
    : game.player1Accepted;

  // Format result display
  const showResult = game.status === "finished";

  let resultLabel = "";
  let resultColor = "";
  let resultIcon = "";
  const winnerUid = resolveWinnerUid(game);

  if (showResult) {
    if (winnerUid === "draw") {
      resultLabel = "It's a Draw!";
      resultColor = "text-yellow-300";
      resultIcon = "🤝";
    } else if (game.winReason === "forfeit") {
      resultLabel = winnerUid === user.uid ? "Opponent Forfeited" : "You Forfeited";
      resultColor = winnerUid === user.uid ? "text-lime-400" : "text-pink-400";
      resultIcon = winnerUid === user.uid ? "🏆" : "🚪";
    } else if (game.winReason === "opponent_timeout") {
      resultLabel = winnerUid === user.uid ? "Opponent Timed Out" : "Timeout Loss";
      resultColor = winnerUid === user.uid ? "text-lime-400" : "text-pink-400";
      resultIcon = winnerUid === user.uid ? "⏱️" : "⌛";
    } else if (winnerUid === user.uid) {
      resultLabel = "You Win!";
      resultColor = "text-lime-400";
      resultIcon = "🏆";
    } else {
      resultLabel = "You Lose";
      resultColor = "text-pink-400";
      resultIcon = "😢";
    }
  }

  async function updateUserStats(
    uid,
    { iqField, winField, cashField, iq, win, cash }
  ) {
    const userRef = doc(db, "users", uid);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(userRef);
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const nextIQ = +(Number(data[iqField] || 0) + iq).toFixed(2);
      const nextWins = (data[winField] || 0) + win;
      const nextCash = +(Number(data[cashField] || 0) + cash).toFixed(2);

      transaction.update(userRef, {
        [iqField]: nextIQ,
        [winField]: nextWins,
        [cashField]: nextCash,
        overall_iq: +(Number(data.overall_iq || 0) + iq).toFixed(2),
        overall_wins: (data.overall_wins || 0) + win,
        overall_cash: +(Number(data.overall_cash || 0) + cash).toFixed(2),
      });
    });
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
      <div
        className="flex w-full max-w-xl flex-col items-center rounded-3xl border-4 border-lime-400/80 bg-slate-900 p-4 shadow-xl sm:p-10"
        style={{ boxShadow: "0 0 40px #84e76a33" }}
      >
        {/* Title & Bet */}
        <div className="text-3xl md:text-4xl font-extrabold text-lime-400 mb-2 text-center">
          Rock Paper Scissors
        </div>
        <div className="text-lg md:text-xl text-slate-300 mb-8 text-center">
          Bet Amount:{" "}
          <span className="text-lime-300 font-bold">{game.betAmount} USDT</span>
        </div>

        {/* Show waiting message if entry not paid */}
        {(!entryPaid || !otherEntryPaid) && (
          <div className="mb-4 text-white text-center">
            {deducting
              ? "Processing entry fee…"
              : !entryPaid
              ? "Confirming your entry fee…"
              : "Waiting for opponent to pay the entry fee…"}
          </div>
        )}

        {/* RPS buttons */}
        <div className="mb-8 grid w-full grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-6">
          {["rock", "paper", "scissors"].map((move) => (
            <button
              key={move}
              className={`
                flex h-36 w-full flex-col items-center justify-center sm:h-44 md:h-48
                rounded-2xl border-4 font-extrabold text-2xl shadow-xl transition-all
                ${
                  myMove === move || game.moves?.[myKey] === move
                    ? "bg-lime-900/70 border-lime-400 scale-105"
                    : "bg-slate-800 border-lime-400/50 hover:border-lime-400 hover:bg-slate-800/80"
                }
                text-lime-200 hover:text-lime-100
              `}
              disabled={
                !!game.moves?.[myKey] ||
                game.status === "finished" ||
                !entryPaid ||
                !otherEntryPaid
              }
              onClick={() => {
                setMyMove(move);
                updateDoc(doc(db, "games", gameId), {
                  [`moves.${myKey}`]: move,
                  updatedAt: serverTimestamp(),
                });
                toast.success(`You picked ${move}!`);
              }}
            >
              <span className="text-2xl md:text-3xl font-extrabold mb-3">
                {move.charAt(0).toUpperCase() + move.slice(1)}
              </span>
              <span className="text-5xl md:text-6xl">
                {move === "rock" ? "🪨" : move === "paper" ? "📄" : "✂️"}
              </span>
            </button>
          ))}
        </div>

        {/* Opponent */}
        <div className="mb-6 text-white text-lg text-center">
          Opponent:{" "}
          <span className="text-lime-300 font-semibold">
            {opponent || "Waiting…"}
          </span>
        </div>

        {/* Game result */}
        {showResult && (
          <div className="flex flex-col items-center gap-2 mb-6">
            <div
              className={`text-2xl md:text-3xl font-extrabold flex items-center gap-2 ${resultColor}`}
            >
              {resultLabel} <span>{resultIcon}</span>
            </div>
            <div className="text-base md:text-lg text-slate-100 mt-2 text-center">
              <span className="font-semibold">You:</span> {game.moves?.[myKey] || "-"}
              <br />
              <span className="font-semibold">Opponent:</span>{" "}
              {game.moves?.[oppKey] || "-"}
            </div>
          </div>
        )}

        {/* Exit Button */}
        <button
          className="mt-7 px-8 py-2 bg-lime-400 rounded-xl font-bold shadow hover:bg-lime-300"
          onClick={() => {
            if (game.status === "active") {
              setForfeitModal(true);
            } else {
              setCurrentGameId(null);
            }
          }}
        >
          Back to Lobby
        </button>
        <ExitConfirmModal
          open={forfeitModal}
          onClose={() => setForfeitModal(false)}
          onConfirm={handleForfeit}
          gameName="rps"
        />
      </div>
    </div>
  );
}
